import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';

import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { get, setLanguage } from '@rover/react-lib/src/apis/frontendConfiguration.api';
import fetchFrontendConfigurationResponseFactory from '@rover/react-lib/src/factories/fetchFrontendConfigurationResponseFactory';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck/createDuck';
import type {
  ActionCreator,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck/types';
import type { ApiFrontendConfigurationRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import ObservabilityMetadata from '@rover/rsdk/src/modules/ObservabilityMetadata';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';

import generateActions from './generateActions';

const DEFAULT_CC_TLD = 'US';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchFrontendConfigurationResponse: ApiFrontendConfigurationRetrieve200;
};

type Actions = {
  fetchFrontendConfigurationPending: ActionCreator<void>;
  fetchFrontendConfigurationFailure: ActionCreator<string>;
  fetchFrontendConfigurationSuccess: ActionCreator<ApiFrontendConfigurationRetrieve200>;
  setLanguage: ActionCreator<{
    language: string;
  }>;
};

type Effects = {
  fetchFrontendConfiguration: EffectCreator<void>;
  setLanguage: EffectCreator<{
    language: string;
  }>;
};

type Selectors = {
  getFrontendConfiguration: SelectorCreator<ApiFrontendConfigurationRetrieve200>;
  getFlags: SelectorCreator<Record<string, boolean>>;
  getSettings: SelectorCreator<ApiFrontendConfigurationRetrieve200['settings']>;
  isFlagActive: SelectorCreator<(arg0: string) => boolean>;
  getDateTimeFormatMap: SelectorCreator<Record<string, string>>;
  getLanguage: SelectorCreator<string>;
  getCcTld: SelectorCreator<string>;
  getIsBeforeHoliday: SelectorCreator<boolean>;
  getCsrfCookieName: SelectorCreator<string>;
  getPriceTransparencyLevel: SelectorCreator<PriceTransparencyLevel>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'frontendConfiguration',
  initialState: {
    loading: false,
    errorMessage: '',
    fetchFrontendConfigurationResponse: fetchFrontendConfigurationResponseFactory(),
  },
  actions: {
    ...generateActions('fetchFrontendConfiguration'),
    setLanguage:
      ({ language }) =>
      (state) =>
        set(cloneDeep(state), 'fetchFrontendConfigurationResponse.settings.language', language),
  },
  effects: {
    fetchFrontendConfiguration:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchFrontendConfigurationPending,
          fetchFrontendConfigurationSuccess,
          fetchFrontendConfigurationFailure,
        }
      ) => {
        dispatch(fetchFrontendConfigurationPending());

        try {
          const fetchFrontendConfigurationResponse: ApiFrontendConfigurationRetrieve200 =
            await get();
          // this is the earliest and most common spot to set this ObservabilityMetadata prop
          ObservabilityMetadata.set('locale', fetchFrontendConfigurationResponse.settings.language);
          await dispatch(fetchFrontendConfigurationSuccess(fetchFrontendConfigurationResponse));
        } catch (e) {
          await dispatch(fetchFrontendConfigurationFailure((e as Error).message));
          captureError(e as Error);
        }
      },
    setLanguage:
      ({ language }) =>
      async (dispatch, getState, duckActions) => {
        dispatch(
          duckActions.setLanguage({
            language,
          })
        );
        await setLanguage({
          language,
        });

        // Note - we need to refresh the page here in order to make sure everything
        // gets rerendered in the selected language
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        if (window && window.location) {
          // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
          window.location.reload();
        }
      },
  },
  selectors: {
    getFrontendConfiguration: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchFrontendConfigurationResponse),
    getFlags: (getState, createSelector, { getFrontendConfiguration }) =>
      createSelector([getFrontendConfiguration], (x) => x.flags),
    getSettings: (getState, createSelector, { getFrontendConfiguration }) =>
      createSelector([getFrontendConfiguration], (x) => x.settings),
    isFlagActive: (getState, createSelector, { getFlags }) =>
      createSelector([getFlags], (flags) => (flagToCheck) => !!flags[flagToCheck]),
    getLanguage: (getState, createSelector, { getSettings }) =>
      createSelector([getSettings], (settings) => settings.language || DEFAULT_LANG),
    getDateTimeFormatMap: (getState, createSelector, { getLanguage }) =>
      createSelector([getLanguage], (language) => getDateTimeFormatMapForLang(language)),
    getCcTld: (getState, createSelector, { getSettings }) =>
      createSelector([getSettings], (settings) => settings.ccTld || DEFAULT_CC_TLD),
    getIsBeforeHoliday: (getState, createSelector, { getSettings }) =>
      createSelector([getSettings], (settings) => settings.isBeforeHoliday),
    getCsrfCookieName: (getState, createSelector, { getSettings }) =>
      createSelector([getSettings], (settings) => settings.csrfCookieName),
    getPriceTransparencyLevel: (getState, createSelector, { getSettings }) =>
      createSelector([getSettings], (settings) => settings.priceTransparencyLevel),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options);
